import { z } from 'zod';
import { DateTime } from 'luxon';
import { GetAcademicYear } from '../utils/semester';
import {
  CheckStudentBaseData,
  CheckSemester,
  CheckDate,
  CheckCode,
} from '../utils/checkFormat';
import { zodStringToDateTime } from '../utils/zod';
import { dateTimeFromString } from '../utils/date';
import { ClassStudentZod } from './Class';

export const PhiSightZod = ClassStudentZod.merge(
  z.object({
    /* 身分證	學號	年級班級座號	學期	
    散瞳治療	
    裸視右眼	裸視左眼	戴鏡右眼	戴鏡左眼	
    散瞳
    近視    近視右眼	近視左眼    遠視	遠視右眼	遠視左眼
    散光	散光右眼	散光左眼				弱視	其他	複檢無異狀	備註	
    醫師建議處置	定期檢查 */
    // pid: z.string(),
    // sid: z.string(),
    // grade: z.string(),
    // no: z.string(),
    // seat: z.string(),
    sem: z.string(),
    year: z.number(),
    sight0L: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),
    sight0R: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),
    sightL: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),
    sightR: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),

    eNear: z.boolean().default(false),
    eNearL: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),
    eNearR: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),
    eFar: z.boolean().default(false),
    eFarL: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),
    eFarR: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),
    eSan: z.boolean().default(false),
    eSanL: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),
    eSanR: z
      .number()
      .nullish()
      .transform((v) => (v == 0 ? null : v)),
    eWeak: z.boolean().default(false),
    eSight99: z.boolean().default(false),
    // eSight99State: z.string().nullish(),

    manageId: z.array(z.string()).nullish(),
    // manage: z.string().nullish(),
    // diopL: z.number().nullish().transform((v) => (v == 0 ? null : v)),
    // diopR: z.number().nullish().transform((v) => (v == 0 ? null : v)),
    noProblem: z.boolean().default(false),
    isDilated: z.boolean().default(false),
    isDilating: z.boolean().default(false),
    periodical: zodStringToDateTime().nullish(),
    examDate: zodStringToDateTime(),
    yearClassId: z.number(),
    measureResult: z.string().nullish(),
  })
);

export type PhiSight = z.infer<typeof PhiSightZod>;

export const PhiSightFileZod = z
  .object({
    /* 身分證、學號、年級班級座號、學期、
    散瞳治療、裸視右眼、裸視左眼、戴鏡右眼、戴鏡左眼、
    散瞳、近視右眼、近視左眼、遠視右眼、遠視左眼、散光右眼、散光左眼、
    近視、遠視、散光、弱視、其他、複檢無異狀、備註、醫師建議處置、定期檢查
      */
    身分證: z.string().nullish(),
    學號: z.string().nullish(),
    年級班級座號: z.string().nullish(),
    學期: z.string().nullish(),
    散瞳治療: z.string().nullish(),
    裸視右眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    裸視左眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    戴鏡右眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    戴鏡左眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    散瞳: z.string().nullish(),
    近視右眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    近視左眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    遠視右眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    遠視左眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    散光右眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    散光左眼: z
      .union([z.string(), z.number()])
      .transform((s) => +s)
      .nullish(),
    近視: z.string().nullish(),
    遠視: z.string().nullish(),
    散光: z.string().nullish(),
    弱視: z.string().nullish(),
    其他: z.string().nullish(),
    複檢無異狀: z.string().nullish(),
    備註: z.string().nullish(),
    醫師建議處置: z.string().nullish(),
    定期檢查: z.string().nullish(),
  })
  .transform((arg) => {
    let _errorMsg = '';
    let _okUpload = true;

    //檢查【身分證】、【學號】、【年級班級座號】格式
    const checkStudentBaseDataResult = CheckStudentBaseData(
      arg.身分證,
      arg.學號,
      arg.年級班級座號
    );
    const _pid = checkStudentBaseDataResult.pid;
    const _sid = checkStudentBaseDataResult.sid;
    const _grade = checkStudentBaseDataResult.grade;
    const _no = checkStudentBaseDataResult.no;
    const _seat = checkStudentBaseDataResult.seat;
    if (!checkStudentBaseDataResult.okUpload) {
      _okUpload = checkStudentBaseDataResult.okUpload;
      _errorMsg += checkStudentBaseDataResult.errorMsg;
    }

    const checkSemester = CheckSemester(arg.學期);
    const _semester = checkSemester.semester;
    if (!checkSemester.okUpload) {
      _okUpload = checkSemester.okUpload;
      _errorMsg += checkSemester.errorMsg;
    }
    const manageIDMap: Record<string, string> = {
    '1': '長效散瞳劑\n(阿托平 Atropine)',
    '2': '短效散瞳劑',
    '3': '其他藥物',
    '4': '配鏡矯治',
    '5': '更換鏡片',
    '6': '遮眼治療',
    '7a': '配戴隱形眼鏡(軟式)',
    '7b': '配戴隱形眼鏡(硬式)',
    '7c': '配戴隱形眼鏡(軟、硬式)',
    '8': '角膜塑型片',
    '9': '視力保健衛教',
    '10': '其他',
    '11': '定期檢查',
  };

    //散瞳治療
    const checkIsDilated = CheckCode(arg.散瞳治療, ['0', '1'], '散瞳治療');
    let isDilated = '';
    if (!checkIsDilated.okUpload && checkIsDilated.code) {
      _okUpload = checkIsDilated.okUpload;
      _errorMsg += checkIsDilated.errorMsg;
    }
    isDilated = checkIsDilated.code;
    const _isDilated = isDilated == '1' ? true : false;
    const sight0Code = [-9, -8, -7, -6, -5, -1, null];

    //裸視右眼
    const _sight0R = arg.裸視右眼 == null ? null : arg.裸視右眼;
    if (_sight0R != null) {
      if (_sight0R >= 1 && _sight0R <= 10) {
        // _sight0R = _sight0R / 10;
      } else if (_sight0R > 10 && _sight0R <= 20) {
      //  _sight0R = +(_sight0R / 10).toFixed(1);
      } else if(!sight0Code.includes(_sight0R))  {
        _okUpload = false;
        _errorMsg += '【裸視右眼】輸入數值範圍1-20，';
      }
    }

    //裸視左眼
    const _sight0L = _sight0R === -9 ? null : arg.裸視左眼 == null ? null : arg.裸視左眼;
    if (_sight0L != null) {
        if (_sight0L >= 1 && _sight0L <= 10) {
          // _sight0L = _sight0L / 10;
        } else if (_sight0L > 10 && _sight0L <= 20) {
          // _sight0L = +(_sight0L / 10).toFixed(1);
        } else if(!sight0Code.includes(_sight0L)) {
          _okUpload = false;
          _errorMsg += '【裸視左眼】輸入數值範圍1-20，';
        }
      }

    //戴鏡右眼
    const _sightR = _sight0R === -9 ? null : (arg.戴鏡右眼 == null || arg.戴鏡右眼 == 0) ? null : arg.戴鏡右眼;
    
    if (_sightR != null) {
      if (_sightR >= 1 && _sightR < 10) {
        // _sightR = _sightR / 10;
      } else if (_sightR >= 10 && _sightR <= 20) {
        // _sightR = +(_sightR / 10).toFixed(1);
      } else if (!sight0Code.includes(_sightR)) {
        _okUpload = false;
        _errorMsg += '【戴鏡右眼】輸入數值範圍1-20，';
      }
    }

    //戴鏡左眼
    const _sightL = _sight0R === -9 ? null : (arg.戴鏡左眼 == null || arg.戴鏡左眼 == 0) ? null : arg.戴鏡左眼;
    
    if (_sightL != null) {
      if (_sightL >= 1 && _sightL < 10) {
        // _sightL = _sightL / 10;
      } else if (_sightL >= 10 && _sightL <= 20) {
        // _sightL = +(_sightL / 10).toFixed(1);
      } else if(!sight0Code.includes(_sightL)) {
        _okUpload = false;
        _errorMsg += '戴鏡左眼】輸入數值範圍1-20，';
      }
    }

    //散瞳
    const checkIsDilating = CheckCode(arg.散瞳, ['0', '1'], '散瞳');
    let isDilating = '';
    if (!checkIsDilating.okUpload && !checkIsDilating.isNull) {
      _okUpload = checkIsDilating.okUpload;
      _errorMsg += checkIsDilating.errorMsg;
    }
    isDilating = checkIsDilating.code;
    if (checkIsDilating.isNull) {
      isDilating = '0';
    }
    const _isDilating = isDilating == '1' ? true : false;

    //近視右眼
    const _eNearR = arg.近視右眼 || null;
    if (!_eNearR && _eNearR != null) {
      if (_eNearR < 1 || _eNearR > 5000) {
        _okUpload = false;
        _errorMsg += '請確認【近視右眼】數值，';
      }
    }

    //近視左眼
    const _eNearL = arg.近視左眼 || null;
    if (!_eNearL && _eNearL != null) {
      if (_eNearL < 1 || _eNearL > 5000) {
        _okUpload = false;
        _errorMsg += '請確認【近視左眼】數值，';
      }
    }

    //遠視右眼
    const _eFarR = arg.遠視右眼 || null;
    if (!_eFarR && _eFarR != null) {
      if (_eFarR < 1 || _eFarR > 2500) {
        _okUpload = false;
        _errorMsg += '請確認【遠視右眼】數值，';
      }
    }

    //遠視左眼
    const _eFarL = arg.遠視左眼 || null;
    if (!_eFarL && _eFarL != null) {
      if (_eFarL < 1 || _eFarL > 2500) {
        _okUpload = false;
        _errorMsg += '請確認【遠視左眼】數值，';
      }
    }

    //散光右眼
    let _sanR = arg.散光右眼;
    if (_sanR && _sanR != null) {
      _sanR = Math.abs(_sanR);
      if (_sanR < 1 || _sanR > 2500) {
        _okUpload = false;
        _errorMsg += '請確認【散光右眼】數值，';
      }
      //輸入值轉負值儲存
      _sanR = -_sanR;
    }

    //散光左眼
    let _sanL = arg.散光左眼;
    if (_sanL && _sanL != null) {
      _sanL = Math.abs(_sanL);
      if (_sanL < 1 || _sanL > 2500) {
        _okUpload = false;
        _errorMsg += '請確認【散光右眼】數值，';
      }
      //輸入值轉負值儲存
      _sanL = -_sanL;
    }

    //近視
    //「近視右」或「近視左」有資料時，則「近視否」(Table 欄位)為 1
    let _near = arg.近視 == '1' ? true : false;
    if (
      (_eNearR == null && _eNearL == null) ||
      (_eNearR == 0 && _eNearL == 0)
    ) {
      // //_near = false
      // if (_near == true) {
      //   _errorMsg += '請確認是否【近視】，';
      //   _okUpload = false;
      // }
    } else {
      _near = true;
    }

    //遠視
    //「遠視右」或「遠視左」有資料時，則「遠視否」(Table 欄位)為 1
    let _far = arg.遠視 == '1' ? true : false;
    if ((_eFarR == null && _eFarL == null) || (_eFarR == 0 && _eFarL == 0)) {
      //_far = false
      // if (_far == true) {
      //   _errorMsg += '請確認是否【遠視】，';
      //   _okUpload = false;
      // }
    } else {
      _far = true;
    }

    //散光
    //「散光右」或「散光左」有資料時，則「散光否」(Table 欄位)為 1
    let _san = arg.散光 == '1' ? true : false;
    if ((_sanR == null && _sanL == null) || (_sanR == 0 && _sanL == 0)) {
      // san = false
      // if (_san == true) {
      //   _errorMsg += '請確認是否【散光】，';
      //   _okUpload = false;
      // }
    } else {
      _san = true;
    }

    //弱視
    const _weak = arg.弱視 == '1' ? true : false;
    //其他
    const _eSight99 = arg.其他 == '1' ? true : false;
    //複檢無異狀
    let _recheck = arg.複檢無異狀 == '1' ? true : false;
    // 「裸視右」、 「裸視左」為 -8、-7、-6，則「複檢無異狀」取消選取
    if (_sight0R != null && _sight0L != null) {
      if ([-8, -7, -6].includes(_sight0R) || [-8, -7, -6].includes(_sight0L)) {
        _recheck = false;
      }
    }
    // 「近視」、 「遠視」、 「散光」、「弱視」、「其他」如有選取，則「複檢無異狀」取消選取
    if (_near || _far || _san || _weak || _eSight99) {
      _recheck = false;
    }

    //備註
    //醫師建議處置
    const _manageId =
      arg.醫師建議處置 == null ? null : arg.醫師建議處置.replaceAll('.', ',').replaceAll('、', ',').replaceAll('，', ',').split(',');
    const _manageFirstId = _manageId?.length ? _manageId[0] : null;
    //定期檢查
    const periodicalDateTime = dateTimeFromString(arg.定期檢查 || '');
    if (!periodicalDateTime.isValid && arg.定期檢查) {
      _errorMsg += '【定期檢查】格式有誤，';
      _okUpload = false;
    }
    const _periodical = arg.定期檢查;

    //移除最後一個'，'
    if (_errorMsg.length != 0) {
      _errorMsg = _errorMsg.slice(0, -1);
    }

    return {
      pid: _pid,
      sid: _sid,
      grade: _grade,
      no: _no,
      seat: _seat,
      sem: _semester,
      year: GetAcademicYear,
      sight0R: _sight0R, //裸視右
      sight0L: _sight0L, //裸視左
      sightR: _sightR, //戴鏡右
      sightL: _sightL, //戴鏡左
      isDilating: _isDilating,
      isDilated: _isDilated,
      eNearR: arg.近視右眼, //近視右
      eNearL: arg.近視左眼, //近視左
      eFarR: arg.遠視右眼, //遠視右
      eFarL: arg.遠視左眼, //遠視左
      eSanR: _sanR, //散光右
      eSanL: _sanL, //散光左
      eNear: _near, //近視
      eFar: _far, //遠視
      eSan: _san, //散光
      eWeak: arg.弱視 == '1' ? true : false, //弱視

      noProblem: _recheck, //複檢無異狀
      eSight99: _eSight99,
      eSight99State: arg.備註 || null,
      manageId: _manageId, //醫師建議處置
      manage: _manageFirstId ? manageIDMap[_manageFirstId] : null,
      periodical: _periodical, //定期檢查
      examDate: null as DateTime | null,
      yearClassId: 0,
      okUpload: _okUpload,
      remark: _errorMsg,
    };
  });

export type PhiSightFile = z.infer<typeof PhiSightFileZod>;
